import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';

const PrintroomReadyButton = ({ jobId, orderNumber, onTrigger, className = "" }) => {
  const [isReady, setIsReady] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const [isTriggering, setIsTriggering] = useState(false);
  const [readinessData, setReadinessData] = useState(null);
  const [notificationStatus, setNotificationStatus] = useState(null);
  const { isAuthenticated } = useAuth();

  const UPLOAD_API_URL = process.env.REACT_APP_UPLOAD_API_URL || '';

  const checkReadiness = async () => {
    if (!jobId || !orderNumber) return;
    
    setIsChecking(true);
    try {
      const response = await fetch(`${UPLOAD_API_URL}/api/printroom/check-readiness`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          job_id: jobId,
          order_number: orderNumber
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setReadinessData({
          ready: data.ready || false,
          has_jobsheet: data.has_jobsheet,
          has_artwork: data.has_artwork,
          total_files: data.total_files,
          error: data.error
        });
        setNotificationStatus({
          notification_sent: data.notification_sent || false,
          notification_count: data.notification_count || 0,
          last_notification: data.last_notification,
          notifications: data.notifications || []
        });
        setIsReady(data.ready || false);
      }
    } catch (error) {
      console.error('Error checking readiness:', error);
    } finally {
      setIsChecking(false);
    }
  };

  const triggerPrintroom = async () => {
    setIsTriggering(true);
    try {
      const response = await fetch(`${UPLOAD_API_URL}/api/printroom/notify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          job_id: jobId,
          order_number: orderNumber,
          trigger_type: 'manual'
        })
      });
      
      const result = await response.json();
      if (result.success) {
        onTrigger && onTrigger(result);
        // Refresh status after successful trigger
        setTimeout(checkReadiness, 1000);
      } else {
        console.error('Printroom notification failed:', result.message);
      }
    } catch (error) {
      console.error('Error triggering printroom:', error);
    } finally {
      setIsTriggering(false);
    }
  };

  useEffect(() => {
    if (jobId && orderNumber) {
      checkReadiness();
    }
  }, [jobId, orderNumber]);

  if (!orderNumber) {
    return null; // Don't show if no PrintLogic order
  }

  return (
    <div className={`p-4 bg-blue-50 border border-blue-200 rounded-lg ${className}`}>
      <h3 className="font-medium text-blue-900 mb-2">Printroom Status</h3>
      
      {isChecking ? (
        <p className="text-blue-700">Checking readiness...</p>
      ) : readinessData ? (
        <div>
          <div className="space-y-1 text-sm text-blue-800 mb-3">
            <p>✓ Jobsheet: {readinessData.has_jobsheet ? 'Present' : 'Missing'}</p>
            <p>✓ Artwork: {readinessData.has_artwork ? 'Present' : 'Missing'}</p>
            <p>Total files: {readinessData.total_files}</p>
          </div>
          
          {notificationStatus?.notification_sent ? (
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  ✓ Files Sent to Printroom
                </span>
              </div>
              {notificationStatus.last_notification && (
                <p className="text-sm text-green-700">
                  Last sent: {new Date(notificationStatus.last_notification.iso_timestamp).toLocaleString()}
                  {notificationStatus.notification_count > 1 && (
                    <span className="text-green-600"> ({notificationStatus.notification_count} times)</span>
                  )}
                </p>
              )}
              <button
                disabled={true}
                className="w-full px-4 py-2 rounded font-medium text-white bg-gray-400 cursor-not-allowed"
              >
                Already Sent to Printroom
              </button>
            </div>
          ) : isReady ? (
            <button
              onClick={triggerPrintroom}
              disabled={isTriggering}
              className={`w-full px-4 py-2 rounded font-medium text-white ${
                isTriggering 
                  ? 'bg-green-300 cursor-not-allowed' 
                  : 'bg-green-600 hover:bg-green-700'
              }`}
            >
              {isTriggering ? 'Sending to Printroom...' : 'Ready for Print - Send to Printroom'}
            </button>
          ) : (
            <p className="text-orange-700 text-sm">
              Waiting for required files...
            </p>
          )}
        </div>
      ) : (
        <p className="text-blue-700">Unable to check status</p>
      )}
    </div>
  );
};

export default PrintroomReadyButton;
